/**
 * Test Call Dialog
 *
 * Small dialog triggered from the profile menu "Test Call" item.
 * Creates a test call to the user's extension via Asterisk and
 * provides a "Hang Up" button to end it.
 */

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import CloseIcon from '@mui/icons-material/Close';
import CallEndIcon from '@mui/icons-material/CallEnd';
import PhoneIcon from '@mui/icons-material/Phone';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useCreateTestCall, useEndTestCall } from '@/modules/user/application';
import { useToast } from '@/shared/components';
import { AppException } from '@/core/exceptions';

// ============================================================================
// Types
// ============================================================================

export interface TestCallDialogProps {
  open: boolean;
  onClose: () => void;
}

type CallState = 'idle' | 'calling' | 'in_progress' | 'hanging_up';

// ============================================================================
// Component
// ============================================================================

export function TestCallDialog({ open, onClose }: TestCallDialogProps) {
  const toast = useToast();
  const [callState, setCallState] = useState<CallState>('idle');
  const channelIdRef = useRef<string | null>(null);

  const { mutate: createTestCall } = useCreateTestCall();
  const { mutate: endTestCall } = useEndTestCall();

  const getErrorMessage = (error: unknown): string => {
    if (error instanceof AppException) return error.userMessage;
    if (error instanceof Error) return error.message;
    return 'An unexpected error occurred';
  };

  // Auto-initiate the test call when dialog opens
  useEffect(() => {
    if (!open) return;

    setCallState('calling');
    channelIdRef.current = null;

    createTestCall(undefined, {
      onSuccess: (data) => {
        channelIdRef.current = data.channel_id;
        setCallState('in_progress');
        toast.success('Test call created — your phone should be ringing');
      },
      onError: (error) => {
        toast.error(getErrorMessage(error));
        setCallState('idle');
        onClose();
      },
    });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleHangUp = useCallback(() => {
    const channelId = channelIdRef.current;
    if (!channelId) return;

    setCallState('hanging_up');
    endTestCall(channelId, {
      onSuccess: () => {
        toast.success('Test call ended');
        channelIdRef.current = null;
        setCallState('idle');
        onClose();
      },
      onError: (error) => {
        // "Call already ended" is not a real error — treat it as success
        toast.info(getErrorMessage(error));
        channelIdRef.current = null;
        setCallState('idle');
        onClose();
      },
    });
  }, [endTestCall, toast, onClose]);

  const handleClose = useCallback(() => {
    // If a call is in progress, hang up first
    if (callState === 'in_progress' && channelIdRef.current) {
      handleHangUp();
      return;
    }
    // Otherwise just close (idle or already hanging up)
    if (callState === 'idle') {
      onClose();
    }
  }, [callState, handleHangUp, onClose]);

  // Reset state when dialog fully closes
  useEffect(() => {
    if (!open) {
      setCallState('idle');
      channelIdRef.current = null;
    }
  }, [open]);

  const isBusy = callState === 'calling' || callState === 'hanging_up';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        Test Call
        <IconButton aria-label="Close" onClick={handleClose} size="small" disabled={isBusy}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 2 }}>
          {callState === 'calling' && (
            <>
              <CircularProgress size={40} />
              <Typography color="text.secondary">Initiating test call...</Typography>
            </>
          )}

          {callState === 'in_progress' && (
            <>
              <PhoneIcon color="success" sx={{ fontSize: 40 }} />
              <Typography color="text.secondary">Test call in progress — check your phone</Typography>
            </>
          )}

          {callState === 'hanging_up' && (
            <>
              <CircularProgress size={40} />
              <Typography color="text.secondary">Ending call...</Typography>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {callState === 'in_progress' && (
          <Button variant="contained" color="error" startIcon={<CallEndIcon />} onClick={handleHangUp} fullWidth>
            Hang Up
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
